import React from "react";
import { Link } from "react-router-dom";
import { COLLECTORS } from "../data/site";
import { Quote, ArrowRight, Sparkles } from "lucide-react";

export default function Collectors() {
  return (
    <div data-testid="collectors-page">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
        <p className="label mb-4">In Good Company</p>
        <h1 className="serif text-5xl md:text-7xl tracking-tight font-light leading-[1.02]">
          Collectors.
        </h1>
        <p className="mt-6 max-w-2xl text-[hsl(var(--ink-soft))] leading-relaxed text-lg">
          A growing community of collectors, hospitality partners, and
          institutions hold Joseph's work in their homes, lobbies, and reading
          rooms. Selected acquirers below — many remain anonymous by request.
        </p>
      </header>

      {COLLECTORS.featured && COLLECTORS.featured.length > 0 && (
        <section className="border-t border-border bg-[hsl(var(--surface))]" data-testid="featured-collectors">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="flex items-center gap-3 mb-10 md:mb-14">
              <Sparkles className="h-4 w-4 text-[hsl(var(--accent))]" />
              <p className="label">Featured Acquisition</p>
            </div>

            {COLLECTORS.featured.map((f) => (
              <article
                key={f.id}
                className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center"
                data-testid={`featured-${f.id}`}
              >
                <figure className="md:col-span-7">
                  <div className="aspect-[4/5] overflow-hidden bg-background">
                    <img
                      src={f.image}
                      alt={f.names.join(" and ")}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </figure>

                <div className="md:col-span-5">
                  <p className="label mb-4">{f.occasion}</p>
                  <h2 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
                    {f.names[0]}
                    {f.names.length > 1 && (
                      <>
                        <br />
                        <span className="serif italic text-[hsl(var(--ink-soft))]">
                          & {f.names.slice(1).join(" & ")}
                        </span>
                      </>
                    )}
                  </h2>
                  <p className="label mt-3">{f.role}</p>
                  <p className="mt-8 text-[hsl(var(--ink-soft))] leading-relaxed text-lg">
                    {f.blurb}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {COLLECTORS.walls && COLLECTORS.walls.length > 0 && (
        <section
          className="border-t border-border"
          data-testid="collectors-walls"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
            <header className="mb-12 md:mb-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
              <div className="md:col-span-7">
                <p className="label mb-3">In Their Homes & At the Gallery</p>
                <h2 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
                  Collectors' walls.
                </h2>
              </div>
              <p className="md:col-span-5 text-[hsl(var(--ink-soft))] text-sm leading-relaxed">
                The work lives where its collectors live. A few moments,
                shared with permission — names kept private where requested.
              </p>
            </header>

            <div className="grid grid-cols-12 gap-4 md:gap-6">
              {COLLECTORS.walls.map((w, i) => {
                // tetris-ish layout: vary spans
                const spans = [
                  "col-span-12 md:col-span-7 md:row-span-2",
                  "col-span-6 md:col-span-5",
                  "col-span-6 md:col-span-5",
                  "col-span-6 md:col-span-6",
                  "col-span-6 md:col-span-6",
                ];
                return (
                  <figure
                    key={w.id}
                    className={`${spans[i] || "col-span-6 md:col-span-4"} art-card`}
                    data-testid={`wall-${w.id}`}
                  >
                    <div className="aspect-square md:aspect-auto md:h-full overflow-hidden bg-[hsl(var(--surface))]">
                      <img
                        src={w.image}
                        alt={w.caption}
                        className="art-img w-full h-full object-cover"
                      />
                    </div>
                    <figcaption className="mt-3">
                      <p className="serif italic text-base md:text-lg leading-tight">
                        {w.caption}
                      </p>
                      <p className="label mt-1">{w.context}</p>
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-4">
            <p className="label">Selected Collections</p>
            <h2 className="serif text-3xl md:text-4xl mt-3 tracking-tight font-light">
              Where the work lives.
            </h2>
          </div>
          <ul className="md:col-span-8 divide-y divide-border border-y border-border">
            {COLLECTORS.notableCollections.map((c, i) => (
              <li
                key={i}
                className="py-5 flex items-baseline gap-6"
                data-testid={`collector-${i}`}
              >
                <span className="serif text-xl text-[hsl(var(--accent))] w-10 shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="serif text-xl md:text-2xl">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-border bg-[hsl(var(--surface))]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="label mb-3">In Their Words</p>
          <h2 className="serif text-3xl md:text-5xl mt-2 tracking-tight font-light mb-12 md:mb-16">
            Testimonials.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {COLLECTORS.testimonials.map((t, i) => (
              <figure
                key={i}
                className="bg-background border border-border p-8 md:p-10"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="h-6 w-6 text-[hsl(var(--accent))] mb-6" />
                <blockquote className="serif italic text-xl md:text-2xl leading-snug text-foreground">
                  {t.quote}
                </blockquote>
                <figcaption className="label mt-8">{t.attribution}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
        <p className="label mb-4">Begin a relationship with the studio</p>
        <h2 className="serif text-4xl md:text-5xl tracking-tight font-light">
          Become a collector.
        </h2>
        <p className="mt-6 max-w-xl mx-auto text-[hsl(var(--ink-soft))]">
          Whether you're acquiring your first piece or building a serious
          contemporary collection, the studio invites a conversation.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/studio-visit"
            className="inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90"
            data-testid="collectors-cta-visit"
          >
            Visit the studio <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 border border-foreground px-8 py-4 text-xs uppercase tracking-[0.18em] hover:bg-foreground hover:text-background transition-colors"
            data-testid="collectors-cta-contact"
          >
            Contact the studio
          </Link>
        </div>
      </section>
    </div>
  );
}
